"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  INPUT,
  INPUT_MONO,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  classifyDegree,
  readModules,
  UK_INTEGRATED_WEIGHTS,
  UK_WEIGHT_PRESETS,
  type UkDegreeResult,
} from "@/lib/tool-kit/grades/uk-degree";

/** English strings. A translated content file overrides these through `ui`. */
export const UK_DEGREE_CLASSIFICATION_CALCULATOR_STRINGS = {
  integratedLabel: "Integrated master's degree (four years)",
  weightingLabel: "Year weighting",
  weightPresets: {
    "0-1-2": "0 : 1 : 2 — final year counts double",
    "0-40-60": "0 : 40 : 60",
    "0-50-50": "0 : 50 : 50",
    "0-30-70": "0 : 30 : 70",
    "0-25-75": "0 : 25 : 75",
    "0-20-80": "0 : 20 : 80",
    "0-0-100": "0 : 0 : 100 — final year only",
    custom: "Custom weights",
  },
  yearWeightLabel: "Year {n} weight",
  integratedHint:
    "Integrated master's weightings vary widely — replace these starting values with your regulations' weights.",
  yearTabsLabel: "Enter marks for",
  yearTab: "Year {n}",
  moduleLabel: "Module",
  modulePlaceholder: "Module {n}",
  creditsLabel: "Credits",
  markLabel: "Mark %",
  addModule: "Add a module",
  removeModule: "Remove",
  removeModuleAria: "Remove module {n} from year {year}",
  yearNotCounted: "Year {n} carries no weight, so its marks do not count.",
  optionsHeading: "Your university's rules",
  discountLabel: "Credits to discount in each counted year",
  discountHint: "0 counts everything. 20 keeps the best 100 of 120 credits.",
  roundLabel:
    "Round the average to a whole mark before classifying (69.5 → 70)",
  zoneLabel: "Borderline zone below each boundary",
  zones: {
    "0": "None",
    "1": "1 mark",
    "2": "2 marks",
    "3": "3 marks",
  },
  results: "Result",
  degreeAverage: "Degree average",
  classification: "Classification",
  classNames: {
    first: "First",
    "upper-second": "Upper second (2:1)",
    "lower-second": "Lower second (2:2)",
    third: "Third",
    "below-third": "Below a Third",
  },
  classifiedOn: "Classified on {value} after rounding.",
  borderline:
    "Borderline: {gap} under the {boundary} boundary for a {cls}. Many universities review cases in this zone, often by preponderance — how much credit sits in the higher class.",
  preponderance:
    "Counted credits with marks of {boundary} or more: {credits} of {total} ({percent}%).",
  belowThirdNote:
    "Below 40 is below honours level: depending on your credits and regulations, a fail or an unclassified (ordinary) degree.",
  missing:
    "Add module marks for every year that carries weight — missing: year {years}.",
  noWeight: "Give at least one year a weight above 0.",
  tableHeading: "How the average is built",
  colYear: "Year",
  colCredits: "Credits counted",
  colAverage: "Year average",
  colWeight: "Weight",
  colShare: "Share of degree",
  notCounted: "Not counted",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "UK degree average {average}: {cls}",
} as const;

type Strings = typeof UK_DEGREE_CLASSIFICATION_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type Preset = keyof Strings["weightPresets"];
type Zone = keyof Strings["zones"];

const presetWeights = (preset: Exclude<Preset, "custom">) =>
  UK_WEIGHT_PRESETS[preset].map(String);
const INTEGRATED_WEIGHTS = UK_INTEGRATED_WEIGHTS.map(String);
const YEAR_NUMBERS = [1, 2, 3, 4] as const;

interface ModuleRow {
  id: number;
  name: string;
  credits: string;
  mark: string;
}

const seed = (year: number, marks: string[]): ModuleRow[] =>
  marks.map((mark, index) => ({
    id: year * 100 + index + 1,
    name: "",
    credits: "30",
    mark,
  }));

const INITIAL_YEARS: ModuleRow[][] = [
  seed(1, ["", "", "", ""]),
  seed(2, ["64", "66", "70", "68"]),
  seed(3, ["70", "72", "66", "68"]),
  seed(4, ["", "", "", ""]),
];

function YearEditor({
  year,
  rows,
  weight,
  onRows,
  nextId,
  strings,
}: {
  year: number;
  rows: ModuleRow[];
  weight: number;
  onRows: (update: (rows: ModuleRow[]) => ModuleRow[]) => void;
  nextId: () => number;
  strings: Strings;
}) {
  function update(id: number, patch: Partial<ModuleRow>) {
    onRows((all) =>
      all.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }
  const cell = (row: ModuleRow, key: "credits" | "mark", label: string) => (
    <div className="w-24 space-y-1">
      <label className={LABEL} htmlFor={`ukd-${key}-${row.id}`}>
        {label}
      </label>
      <input
        className={INPUT_MONO}
        id={`ukd-${key}-${row.id}`}
        inputMode="decimal"
        onChange={(event) => update(row.id, { [key]: event.target.value })}
        type="text"
        value={row[key]}
      />
    </div>
  );
  return (
    <div className="space-y-3">
      {weight <= 0 ? (
        <Notice>{t(strings.yearNotCounted, { n: year })}</Notice>
      ) : null}
      {rows.map((row, index) => (
        <div className="flex flex-wrap items-end gap-3" key={row.id}>
          <div className="min-w-0 flex-1 basis-40 space-y-1">
            <label className={LABEL} htmlFor={`ukd-name-${row.id}`}>
              {strings.moduleLabel}
            </label>
            <input
              className={INPUT}
              id={`ukd-name-${row.id}`}
              onChange={(event) => update(row.id, { name: event.target.value })}
              placeholder={t(strings.modulePlaceholder, { n: index + 1 })}
              type="text"
              value={row.name}
            />
          </div>
          {cell(row, "credits", strings.creditsLabel)}
          {cell(row, "mark", strings.markLabel)}
          <button
            aria-label={t(strings.removeModuleAria, { n: index + 1, year })}
            className={BUTTON_SMALL}
            disabled={rows.length <= 1}
            onClick={() => onRows((all) => all.filter((r) => r.id !== row.id))}
            type="button"
          >
            {strings.removeModule}
          </button>
        </div>
      ))}
      <button
        className={BUTTON_SECONDARY}
        onClick={() =>
          onRows((all) => [
            ...all,
            { id: nextId(), name: "", credits: "30", mark: "" },
          ])
        }
        type="button"
      >
        {strings.addModule}
      </button>
    </div>
  );
}

function YearTable({
  result,
  strings,
  fmt,
}: {
  result: UkDegreeResult;
  strings: Strings;
  fmt: Format;
}) {
  let weightTotal = 0;
  for (const year of result.years) {
    weightTotal += Math.max(0, year.weight);
  }
  const two = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  const share = (weight: number) =>
    weight > 0
      ? `${fmt((weight * 100) / weightTotal, { maximumFractionDigits: 1 })}%`
      : strings.notCounted;
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.colYear,
          strings.colCredits,
          strings.colAverage,
          strings.colWeight,
          strings.colShare,
        ]}
        rows={result.years.map((year, index) => [
          fmt(index + 1),
          two(year.credits),
          year.average === null ? "—" : two(year.average),
          two(year.weight),
          share(year.weight),
        ])}
      />
    </section>
  );
}

function ResultSummary({
  result,
  strings,
  fmt,
}: {
  result: UkDegreeResult;
  strings: Strings;
  fmt: Format;
}) {
  const two = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  const cls = strings.classNames[result.cls];
  const { borderline, preponderance } = result;
  return (
    <>
      <StatGrid>
        <StatCard label={strings.degreeAverage} value={two(result.average)} />
        <StatCard emphasis label={strings.classification} value={cls} />
      </StatGrid>
      {result.classifiedAverage === result.average ? null : (
        <p className="text-muted-foreground text-sm">
          {t(strings.classifiedOn, { value: two(result.classifiedAverage) })}
        </p>
      )}
      {borderline ? (
        <Notice>
          {t(strings.borderline, {
            gap: two(borderline.gap),
            boundary: fmt(borderline.boundary),
            cls: strings.classNames[borderline.cls],
          })}
          {preponderance && preponderance.total > 0
            ? ` ${t(strings.preponderance, {
                boundary: fmt(preponderance.boundary),
                credits: two(preponderance.credits),
                total: two(preponderance.total),
                percent: fmt(
                  (preponderance.credits * 100) / preponderance.total,
                  {
                    maximumFractionDigits: 1,
                  }
                ),
              })}`
            : null}
        </Notice>
      ) : null}
      {result.cls === "below-third" ? (
        <Notice>{strings.belowThirdNote}</Notice>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summary, { average: two(result.average), cls })}
      />
    </>
  );
}

export default function UkDegreeClassificationCalculatorWidget() {
  const strings = useToolUi(UK_DEGREE_CLASSIFICATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const idCounter = useRef(1000);
  const nextId = () => {
    idCounter.current += 1;
    return idCounter.current;
  };
  const [integrated, setIntegrated] = useState(false);
  const [preset, setPreset] = useState<Preset>("0-1-2");
  const [weights, setWeights] = useState<string[]>(presetWeights("0-1-2"));
  const [years, setYears] = useState<ModuleRow[][]>(INITIAL_YEARS);
  const [activeYear, setActiveYear] = useState("3");
  const [discountRaw, setDiscountRaw] = useState("0");
  const [roundWhole, setRoundWhole] = useState(false);
  const [zone, setZone] = useState<Zone>("2");

  const yearCount = integrated ? 4 : 3;
  const weightValues = weights.map((raw) =>
    Math.max(0, parseNumberInput(raw) ?? 0)
  );
  const outcome = classifyDegree(
    weightValues.map((weight, index) => ({
      weight,
      modules: readModules(years[index]),
    })),
    {
      discountCredits: Math.max(0, parseNumberInput(discountRaw) ?? 0),
      roundToWhole: roundWhole,
      zone: Number(zone),
    }
  );
  const active = Math.min(Number(activeYear), yearCount);

  function toggleIntegrated(next: boolean) {
    setIntegrated(next);
    setPreset(next ? "custom" : "0-1-2");
    setWeights(next ? INTEGRATED_WEIGHTS : presetWeights("0-1-2"));
    if (!next && activeYear === "4") {
      setActiveYear("3");
    }
  }

  function choosePreset(next: Preset) {
    setPreset(next);
    if (next !== "custom") {
      setWeights(presetWeights(next));
    }
  }

  const presetOptions = (Object.keys(strings.weightPresets) as Preset[])
    .filter((key) => !integrated || key === "custom")
    .map((key) => ({ value: key, label: strings.weightPresets[key] }));
  const yearTabs = Array.from({ length: yearCount }, (_, index) => ({
    value: String(index + 1),
    label: t(strings.yearTab, { n: index + 1 }),
  }));

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <Checkbox
          checked={integrated}
          label={strings.integratedLabel}
          onChange={toggleIntegrated}
        />
        <SelectField
          id="ukd-preset"
          label={strings.weightingLabel}
          onChange={choosePreset}
          options={presetOptions}
          value={preset}
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {YEAR_NUMBERS.slice(0, weights.length).map((year) => (
            <TextField
              id={`ukd-weight-${year}`}
              key={year}
              label={t(strings.yearWeightLabel, { n: year })}
              numeric
              onChange={(value) => {
                setPreset("custom");
                setWeights((all) =>
                  all.map((weight, i) => (i === year - 1 ? value : weight))
                );
              }}
              value={weights[year - 1] ?? ""}
            />
          ))}
        </div>
        {integrated ? (
          <p className="text-muted-foreground text-xs">
            {strings.integratedHint}
          </p>
        ) : null}
      </div>

      <div className={`${CARD} space-y-4`}>
        <PillGroup
          label={strings.yearTabsLabel}
          name="ukd-year"
          onChange={setActiveYear}
          options={yearTabs}
          value={String(active)}
        />
        <YearEditor
          nextId={nextId}
          onRows={(update) =>
            setYears((all) =>
              all.map((rows, index) =>
                index === active - 1 ? update(rows) : rows
              )
            )
          }
          rows={years[active - 1]}
          strings={strings}
          weight={weightValues[active - 1] ?? 0}
          year={active}
        />
      </div>

      <div className={`${CARD} space-y-4`}>
        <h3 className={WIDGET_H3}>{strings.optionsHeading}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.discountHint}
            id="ukd-discount"
            label={strings.discountLabel}
            numeric
            onChange={setDiscountRaw}
            value={discountRaw}
          />
          <SelectField
            id="ukd-zone"
            label={strings.zoneLabel}
            onChange={setZone}
            options={(Object.keys(strings.zones) as Zone[]).map((key) => ({
              value: key,
              label: strings.zones[key],
            }))}
            value={zone}
          />
        </div>
        <Checkbox
          checked={roundWhole}
          label={strings.roundLabel}
          onChange={setRoundWhole}
        />
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {"result" in outcome ? (
          <ResultSummary fmt={fmt} result={outcome.result} strings={strings} />
        ) : (
          <Notice>
            {outcome.missing.length > 0
              ? t(strings.missing, {
                  years: outcome.missing.map((n) => fmt(n)).join(", "),
                })
              : strings.noWeight}
          </Notice>
        )}
      </section>

      {"result" in outcome ? (
        <YearTable fmt={fmt} result={outcome.result} strings={strings} />
      ) : null}
    </div>
  );
}
